import { useCallback, useState } from "react";
import { toast } from "@jsq/ds/core";
import { useFayDocumentDownloadUrlLazyQuery } from "#graphql/supergraphHooks";

/** The row fields needed to fetch a download URL for a bound document. */
export type DownloadablePackageDocument = {
  documentId: string;
  documentVersion?: number;
  filename?: string;
};

export type UseDownloadPackageDocumentResult = {
  /** documentId currently being downloaded, or null when idle. */
  downloadingDocumentId: string | null;
  download: (document: DownloadablePackageDocument) => Promise<void>;
};

/**
 * Navigate to the presigned URL to start the download. The server's
 * `Content-Disposition: attachment` makes the browser download rather than
 * navigate; the `download` hint is a best-effort filename for same-origin
 * responses (ignored cross-origin, where the disposition governs the name).
 */
function triggerDownload(url: string, filename?: string): void {
  const link = window.document.createElement("a");
  link.href = url;
  if (filename) {
    link.setAttribute("download", filename);
  }
  link.style.display = "none";
  window.document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Downloads the ORIGINAL uploaded file for a bound document (any type — PDFs and
 * non-PDF workbooks alike) via fay's `fayDocumentDownloadUrl`. The presigned URL
 * is signed with an attachment disposition, so navigating to it downloads the
 * file under its original name — a plain anchor navigation, which avoids the
 * cross-origin `fetch` a blob download would need. The URL is short-lived
 * (~300s), so it is fetched lazily on each download rather than up front.
 *
 * One instance serves the whole table; `downloadingDocumentId` lets the caller
 * show a per-row busy state.
 *
 * @param arenaId Undefined until the host app resolves its arena; the download
 * no-ops until then rather than issuing the required arg as NaN.
 */
export function useDownloadPackageDocument(
  arenaId: number | undefined,
): UseDownloadPackageDocumentResult {
  const [fetchDownloadUrl] = useFayDocumentDownloadUrlLazyQuery({
    fetchPolicy: "network-only",
  });
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<string | null>(null);

  const download = useCallback(
    async (document: DownloadablePackageDocument) => {
      if (arenaId === undefined) {
        return;
      }
      setDownloadingDocumentId(document.documentId);
      try {
        const { data } = await fetchDownloadUrl({
          variables: {
            arenaId,
            documentId: document.documentId,
            documentVersion: document.documentVersion,
          },
        });
        const node = data?.fayDocumentDownloadUrl;
        if (!node?.downloadUrl) {
          throw new Error("No download URL returned");
        }
        triggerDownload(node.downloadUrl, node.filename || document.filename);
      } catch {
        toast.error("Error downloading document.");
      } finally {
        setDownloadingDocumentId(null);
      }
    },
    [arenaId, fetchDownloadUrl],
  );

  return { downloadingDocumentId, download };
}
